import React, { useRef, useLayoutEffect, forwardRef, useState, useEffect, useCallback } from 'react'
import { MultiSelect, MultiSelectProps, MultiSelectChangeParams } from 'primereact/multiselect'
import { useExclusiveOverlay } from '@aytm/app/insights/dashboard/components/hooks'
import classNames from 'classnames'
import mcss from './CustomDropdown.module.scss'

export type CustomDropdownChangeParams = MultiSelectChangeParams
export type CustomDropdownType = MultiSelect

type CustomDropdownProps = {
  auxActionIcon: string
  auxActionClassName?: string
  onAuxTriggerClick?(this: void, event: MouseEvent): void
  onLabelClick?(this: void, event?: MouseEvent): void
} & MultiSelectProps

export const CustomDropdown = React.memo(
  forwardRef<MultiSelect, CustomDropdownProps>((props, ref) => {
    const { onAuxTriggerClick, onLabelClick, auxActionIcon, auxActionClassName, className, panelClassName, ...rest } =
      props
    const [isOpenTracking, setIsOpenTracking] = useState(false)
    const ddRef = useRef<MultiSelect>(null)
    const exclusiveOverlay = useExclusiveOverlay()
    const setRef = (instance: MultiSelect | null) => {
      if ('function' === typeof ref) {
        ref(instance)
      } else if (ref) {
        ref.current = instance
      }
      ddRef.current = instance
    }

    const handlePanelOpen = useCallback(() => {
      setIsOpenTracking(true)
      exclusiveOverlay(ddRef.current)
      rest.onShow?.()
    }, [exclusiveOverlay, rest])

    const handlePanelClose = useCallback(() => {
      setIsOpenTracking(false)
      exclusiveOverlay(null)
      rest.onHide?.()
    }, [rest, exclusiveOverlay])

    useEffect(() => {
      if (!ddRef.current) {
        return
      }
      const triggerEl = ddRef.current.getElement().querySelector('.p-multiselect-trigger')
      if (isOpenTracking) {
        if (!triggerEl.querySelector(':is([aria-expanded="true"])')) {
          triggerEl.setAttribute('aria-expanded', 'true')
        }
      } else {
        if (!triggerEl.querySelector(':is([aria-expanded="false"])')) {
          triggerEl.setAttribute('aria-expanded', 'false')
        }
      }
    }, [isOpenTracking])

    useLayoutEffect(() => {
      if (props.disabled || !ddRef.current || !onLabelClick) {
        return void 0
      }
      const labelEl = ddRef.current.getElement().querySelector('.p-multiselect-label-container')

      if (!labelEl) {
        return void 0
      }

      const onLabelClickHandler: EventListener = event => {
        event.preventDefault()
        event.stopPropagation()
        exclusiveOverlay(null)
        onLabelClick?.(event as MouseEvent)
      }

      labelEl.addEventListener('click', onLabelClickHandler)
      return () => {
        labelEl.removeEventListener('click', onLabelClickHandler)
      }
    }, [onLabelClick, props.disabled, exclusiveOverlay])

    useLayoutEffect(() => {
      if (!ddRef.current || !auxActionIcon) {
        return void 0
      }
      if (ddRef.current.getElement().querySelector('.p-multiselect-trigger-clone')) {
        return void 0
      }
      const triggerEl = ddRef.current.getElement().querySelector('.p-multiselect-trigger')
      if (!triggerEl) {
        return void 0
      }
      const clonedTriggerEl = triggerEl.cloneNode() as HTMLElement
      clonedTriggerEl.classList.value =
        'p-multiselect-trigger p-multiselect-trigger-clone ' + (auxActionClassName || '')
      clonedTriggerEl.addEventListener('click', event => {
        event.preventDefault()
        event.stopPropagation()
        onAuxTriggerClick?.(event)
      })
      clonedTriggerEl.innerHTML = `<i class="p-clickable ${auxActionIcon}"></i>` //p-multiselect-trigger-icon p-clickable pi pi-plus

      triggerEl.parentElement.appendChild(clonedTriggerEl)
      return () => {
        triggerEl.parentElement.removeChild(clonedTriggerEl)
      }
    }, [auxActionIcon, auxActionClassName, onAuxTriggerClick])

    return (
      <MultiSelect
        {...rest}
        className={classNames(mcss['custom-dropdown'], className)}
        ref={setRef}
        panelClassName={classNames(mcss['custom-dropdown-panel'], 'PRAYTMTheme', panelClassName)}
        onShow={handlePanelOpen}
        onHide={handlePanelClose}
      />
    )
  })
)

CustomDropdown.displayName = 'CustomDropdown'

export default CustomDropdown
